import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from 'react-query';
import io from 'socket.io-client';
import { AlertTriangle, X, CheckCircle, XCircle } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import './NotificacoesAdmin.css';

const NotificacoesAdmin = () => {
  const { user } = useAuth();
  
  // Buscar estabelecimento se não estiver populado
  const { data: estabelecimentoData } = useQuery(
    ['estabelecimento-admin', user?._id],
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        return null;
      }
    },
    {
      enabled: !!user && (user.tipo === 'admin' || user.tipo === 'estabelecimento'),
      staleTime: 5 * 60 * 1000
    }
  );
  
  // Usar estabelecimento do user ou do query
  const estabelecimentoId = user?.estabelecimento?._id || user?.estabelecimento || estabelecimentoData?._id;
  const [solicitacaoAtual, setSolicitacaoAtual] = useState(null);
  const [socket, setSocket] = useState(null);
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    // Só conectar se for admin
    if (!user || (user.tipo !== 'admin' && user.tipo !== 'estabelecimento')) {
      console.log('❌ NotificacoesAdmin: Usuário não é admin ou estabelecimento', user?.tipo);
      return;
    }

    console.log('🔔 NotificacoesAdmin: Inicializando para user:', user.tipo);

    // Determinar URL do Socket.io - SEMPRE usar window.location.origin em produção
    const socketURL = window.location.origin;
    console.log('🔔 NotificacoesAdmin: Conectando ao Socket.io em:', socketURL);

    const socketConnection = io(socketURL, {
      transports: ['polling', 'websocket'],
      path: '/socket.io/',
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
      timeout: 20000,
      forceNew: false,
      upgrade: true
    });

    // ✅ REGISTRAR LISTENER IMEDIATAMENTE - ANTES DE QUALQUER COISA
    const handlerNovaSolicitacao = (data) => {
      console.log('🔔🔔🔔🔔🔔 NOVA SOLICITAÇÃO RECEBIDA NO ADMIN 🔔🔔🔔🔔🔔');
      console.log('🔔 Tipo:', data.tipo);
      console.log('🔔 Comanda:', data.comanda);
      console.log('🔔 Dados completos:', JSON.stringify(data, null, 2));
      setSolicitacaoAtual(data);
      
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch (e) {}
      
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Nova Solicitação', {
          body: `Solicitação de ${data.tipo === 'cancelar' ? 'cancelamento' : data.tipo === 'editar' ? 'edição' : 'remoção de item'} - Comanda #${data.comanda.numero}`,
          icon: '/logo-cantinho.png'
        });
      }
    };
    
    socketConnection.on('nova-solicitacao-cancelamento', handlerNovaSolicitacao);
    console.log('✅ LISTENER REGISTRADO: nova-solicitacao-cancelamento');

    socketConnection.on('connect', () => {
      console.log('✅✅✅ Admin conectado ao Socket.io ✅✅✅');
      console.log('👤 User completo:', JSON.stringify(user, null, 2));
      console.log('🏢 Estabelecimento do user:', user?.estabelecimento);
      console.log('🏢 Estabelecimento do query:', estabelecimentoData);
      console.log('🏢 Estabelecimento ID final:', estabelecimentoId);
      console.log('🔌 Socket ID:', socketConnection.id);
      
      // Entrar na sala do estabelecimento
      const estabelecimentoIdParaSala = estabelecimentoId;
      if (estabelecimentoIdParaSala) {
        const estabelecimentoIdString = estabelecimentoIdParaSala.toString();
        socketConnection.emit('join-admin', estabelecimentoIdString);
        console.log(`👑👑👑 Admin entrou na sala admin-${estabelecimentoIdString} 👑👑👑`);
      } else {
        console.warn('⚠️⚠️⚠️ Admin não tem estabelecimento vinculado! ⚠️⚠️⚠️');
        console.warn('⚠️ Tentando entrar em todas as salas admin-* como fallback...');
        // Fallback: entrar em sala genérica
        socketConnection.emit('join-admin', 'all');
      }
    });

    socketConnection.on('connect_error', (error) => {
      console.error('❌❌❌ Erro ao conectar Socket.io:', error);
    });

    socketConnection.on('disconnect', (reason) => {
      console.warn('⚠️ Admin desconectado do Socket.io. Motivo:', reason);
    });

    // ✅ Log de qualquer evento recebido (para debug)
    socketConnection.onAny((eventName, ...args) => {
      console.log(`📡📡📡 Evento Socket.io recebido: ${eventName}`, args);
    });

    setSocket(socketConnection);

    return () => {
      socketConnection.disconnect();
    };
    }, [user, estabelecimentoId, estabelecimentoData]);

  const handleAprovar = async () => {
    if (!solicitacaoAtual) return;
    
    setProcessando(true);
    try {
      const response = await api.post(`/api/solicitacoes/${solicitacaoAtual._id}/aprovar`, {
        observacoes: ''
      });
      
      if (response.data.success) {
        const tipoMsg = solicitacaoAtual.tipo === 'cancelar' 
          ? 'cancelamento' 
          : solicitacaoAtual.tipo === 'editar'
          ? 'edição'
          : 'remoção de item';
        toast.success(`Solicitação de ${tipoMsg} aprovada!`);
        setSolicitacaoAtual(null);
        
        // Recarregar página após 1 segundo para atualizar dados
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      toast.error(error.response?.data?.message || 'Erro ao aprovar solicitação');
    } finally {
      setProcessando(false);
    }
  };

  const handleNegar = async () => {
    if (!solicitacaoAtual) return;
    
    const motivo = window.prompt('Motivo da negação (opcional):') || '';
    
    setProcessando(true);
    try {
      const response = await api.post(`/api/solicitacoes/${solicitacaoAtual._id}/negar`, {
        observacoes: motivo
      });
      
      if (response.data.success) {
        toast.success('Solicitação negada');
        setSolicitacaoAtual(null);
      }
    } catch (error) {
      console.error('Erro ao negar:', error);
      toast.error(error.response?.data?.message || 'Erro ao negar solicitação');
    } finally {
      setProcessando(false);
    }
  };

  // Não renderizar se não for admin ou não houver solicitação
  if (!user || (user.tipo !== 'admin' && user.tipo !== 'estabelecimento')) {
    return null;
  }

  if (!solicitacaoAtual) {
    return null;
  }

  return (
    <div className="notificacao-overlay">
      <div className="notificacao-modal">
        <div className="notificacao-header">
          <AlertTriangle size={32} className="icon-alerta" />
          <h2>⚠️ NOVA SOLICITAÇÃO</h2>
          <button 
            className="btn-fechar-notificacao"
            onClick={() => setSolicitacaoAtual(null)}
            disabled={processando}
          >
            <X size={20} />
          </button>
        </div>

        <div className="notificacao-body">
          <div className="info-solicitacao">
            <div className="info-item">
              <strong>Comanda:</strong>
              <span>#{solicitacaoAtual.comanda.numero}</span>
            </div>
            <div className="info-item">
              <strong>Tipo:</strong>
              <span className={`badge-tipo ${solicitacaoAtual.tipo}`}>
                {solicitacaoAtual.tipo === 'cancelar' 
                  ? '🔴 Cancelar' 
                  : solicitacaoAtual.tipo === 'editar'
                  ? '✏️ Editar'
                  : '🗑️ Remover Item'}
              </span>
            </div>
            {solicitacaoAtual.tipo === 'remover_item' && solicitacaoAtual.item && (
              <div className="info-item">
                <strong>Item a remover:</strong>
                <span>{solicitacaoAtual.item.quantidade}x {solicitacaoAtual.item.produto?.nome || 'Produto'} - R$ {solicitacaoAtual.item.subtotal?.toFixed(2) || '0.00'}</span>
              </div>
            )}
            <div className="info-item">
              <strong>Solicitado por:</strong>
              <span>{solicitacaoAtual.solicitadoPor.nome}</span>
            </div>
            <div className="info-item">
              <strong>Motivo:</strong>
              <span>{solicitacaoAtual.motivo || 'Não informado'}</span>
            </div>
            <div className="info-item">
              <strong>Total da Comanda:</strong>
              <span className="valor-total">R$ {solicitacaoAtual.comanda.total?.toFixed(2) || '0.00'}</span>
            </div>
          </div>

          {solicitacaoAtual.comanda.itens && solicitacaoAtual.comanda.itens.length > 0 && (
            <div className="itens-comanda">
              <strong>Itens da Comanda:</strong>
              <ul>
                {solicitacaoAtual.comanda.itens.map((item, idx) => (
                  <li key={idx}>
                    {item.quantidade}x {item.produto?.nome || 'Produto'} - 
                    R$ {item.subtotal?.toFixed(2) || '0.00'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="notificacao-footer">
          <button
            className="btn-negar"
            onClick={handleNegar}
            disabled={processando}
          >
            <XCircle size={20} />
            Negar
          </button>
          <button
            className="btn-aprovar"
            onClick={handleAprovar}
            disabled={processando}
          >
            <CheckCircle size={20} />
            {processando ? 'Processando...' : 'Aprovar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificacoesAdmin;

